import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { AccountOnboardingView, CommandCenterHousehold, GUID, Tenant } from '@ndvr/customer-api';

import { CustomerApi } from 'API';
import { Toast } from 'widgets';

export const AccountOnboardingAdminQueryKeys = {
    newAccountsNotYetOnboarded: ['accountOnboarding', 'newAccountsNotYetOnboarded'] as const,
};

export const useHouseholdsByTenantQuery = (tenantGUID: string | null | undefined) =>
    useQuery<CommandCenterHousehold[]>({
        queryKey: ['tenant', tenantGUID, 'households'],
        queryFn: async () => {
            const [response] = await CustomerApi().getCommandCenterHouseholdList(tenantGUID!);
            return response ?? [];
        },
        enabled: !!tenantGUID,
    });

export const useNewAccountsNotYetOnboardedQuery = () =>
    useQuery<AccountOnboardingView[]>({
        queryKey: AccountOnboardingAdminQueryKeys.newAccountsNotYetOnboarded,
        queryFn: async () => {
            const [response] = await CustomerApi().listNewAccountsNotYetOnboarded();
            return response ?? [];
        },
    });

export const useDismissAccountOnboardingMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (accountNumber: string) => {
            await CustomerApi().dismissAccountOnboarding(accountNumber);
        },
        onSuccess: () => {
            Toast.success('Account onboarding record dismissed.');
            queryClient.invalidateQueries({
                queryKey: AccountOnboardingAdminQueryKeys.newAccountsNotYetOnboarded,
            });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to dismiss the account onboarding record.');
        },
    });
};

export const useAssignAccountToTenantMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ accountNumber, tenantGUID }: { accountNumber: string; tenantGUID: GUID<Tenant> }) => {
            await CustomerApi().assignAccountToTenant(accountNumber, tenantGUID);
        },
        onSuccess: () => {
            Toast.success('Account assigned to tenant.');
            queryClient.invalidateQueries({
                queryKey: AccountOnboardingAdminQueryKeys.newAccountsNotYetOnboarded,
            });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to assign the account to tenant.');
        },
    });
};
